import { useIsFetching } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";

import {
    type BaseActivatedPanelProps,
    ManualSelectionItem,
    type SelectionDropdownItem,
} from "app/pages/sales-intelligence/common-components/table-actions-panel";
import { buildAppsExportData } from "app/pages/sales-intelligence/modules/apps-search/utils/dto";
import { ExportCostTypeItem } from "app/pages/sales-intelligence/modules/quota/enums";
import { useLeadsExportCost } from "app/pages/sales-intelligence/modules/quota/hooks/useLeadsExportCost";
import { useExportQuota } from "app/pages/sales-intelligence/modules/quota/hooks/useExportQuota";
import salesSettingsHelper from "app/pages/sales-intelligence/services/salesSettingsHelper";
import trackingService from "app/pages/sales-intelligence/services/tracking/createMultiSelectorPanelService";
import { setIsRowSelectionEnabled } from "app/pages/sales-intelligence/store/find-apps/action-creators";
import {
    selectActiveAppSourceType,
    selectAppsSearchRequestDto,
    selectSelectedRows,
} from "app/pages/sales-intelligence/store/find-apps/selectors";
import LegacyWebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/LegacyWebsitesExcelExportPanel/LegacyWebsitesExcelExportPanelView";
import WebsitesExcelExportPanelView from "app/pages/sales-intelligence/sub-modules/company-search/components/WebsitesExcelExportPanel/WebsitesExcelExportPanelView";

import appsExportSelectionDropdown from "../../configuration/table-actions/tableExportSelectionDropdown";
import useClearAndCloseRowsSelection from "../../hooks/useClearAndCloseRowsSelection";
import useExcelExportMutation from "../../queries/useExcelExportMutation";
import { findAppsQueryKeyFactory } from "../../query/queryKeyFactory";

type Props = BaseActivatedPanelProps & {
    numberOfResults: number;
};

const AppsExcelExportPanelContainer = ({ numberOfResults, onDeactivate }: Props) => {
    const dispatch = useDispatch();

    const excelQuota = useExportQuota();
    const exportCostState = useLeadsExportCost({ exportType: ExportCostTypeItem.Excel });

    const activeAppSourceType = useSelector(selectActiveAppSourceType);
    const searchRequestDto = useSelector(selectAppsSearchRequestDto);
    const selectedRows = useSelector(selectSelectedRows);

    const { mutateAsync, isPending: IsExcelExportPending } = useExcelExportMutation(
        activeAppSourceType,
        searchRequestDto,
    );

    const isSearchResultsFetching = useIsFetching({
        queryKey: findAppsQueryKeyFactory.appData(activeAppSourceType),
    });

    const isBlockingDataFetching = isSearchResultsFetching > 0 || IsExcelExportPending;

    const clearAndCloseRowsSelection = useClearAndCloseRowsSelection(setIsRowSelectionEnabled);

    const handleDeactivate = (): void => {
        clearAndCloseRowsSelection();
        onDeactivate();
    };

    const handleSelectionChange = (item: Readonly<SelectionDropdownItem>): void => {
        if (item.id === ManualSelectionItem.ID) {
            dispatch(setIsRowSelectionEnabled(true));
        } else {
            clearAndCloseRowsSelection();
        }
    };

    const handleSubmit = async (item: Readonly<SelectionDropdownItem>): Promise<void> => {
        trackingService.trackMultiSelectorExportClick("excel", excelQuota.data.remaining, item.alias, item.value);

        handleDeactivate();

        const selectedAppIds = selectedRows.map((app) => app.app_id);

        await mutateAsync(buildAppsExportData(item, selectedAppIds));
    };

    if (salesSettingsHelper.isDataCreditsEnabled) {
        return (
            <WebsitesExcelExportPanelView
                isDisabled={isBlockingDataFetching}
                isLoading={isBlockingDataFetching}
                numberOfSelectedItems={selectedRows.length}
                numberOfRemainingExports={excelQuota.data.remaining}
                numberOfResults={numberOfResults}
                selectionDropdown={appsExportSelectionDropdown}
                onDeactivate={handleDeactivate}
                onSelectionChange={handleSelectionChange}
                exportCostState={exportCostState}
                onSubmit={handleSubmit}
            />
        );
    }

    return (
        <LegacyWebsitesExcelExportPanelView
            isDisabled={isBlockingDataFetching}
            isLoading={isBlockingDataFetching}
            numberOfSelectedItems={selectedRows.length}
            numberOfRemainingExports={excelQuota.data.remaining}
            numberOfResults={numberOfResults}
            selectionDropdown={appsExportSelectionDropdown}
            onDeactivate={handleDeactivate}
            onSelectionChange={handleSelectionChange}
            onSubmit={handleSubmit}
        />
    );
};

export default AppsExcelExportPanelContainer;
